import { acceptHMRUpdate, defineStore } from "pinia";
import { reactive } from "vue";

import { MatomoHelper } from "@/helpers";
import {
  type DictsysInflectionScheme,
  type DictsysWordclass,
  type DictsysWordclassKey,
  MatomoEventAction,
  MatomoEventCategory
} from "@/types";

interface InflectionSchemeOpenState {
  headWord: string;
  wordclass: DictsysWordclass;
  inflectionScheme: DictsysInflectionScheme;
}

interface State {
  opened: Record<string, InflectionSchemeOpenState>;
}

const availableInflectionSchemes: Record<string, DictsysWordclassKey[]> = {
  de: ["adjective", "article", "noun", "pronoun", "verb"],
  en: ["adjective", "noun", "verb"],
  es: ["verb"],
  fr: ["adjective", "noun", "verb"]
};

export const useDictsysInflectionSchemeStore = defineStore("dictsysInflectionScheme", () => {
  const state = reactive<State>({
    opened: {}
  });

  const canShowInflectionScheme = (
    language: string,
    wordclass: DictsysWordclass,
    inflectionScheme: DictsysInflectionScheme
  ): boolean =>
    inflectionScheme.json !== null &&
    language in availableInflectionSchemes &&
    availableInflectionSchemes[language].indexOf(wordclass.key) !== -1;

  const toggleScheme = (
    id: string,
    headWord: string,
    wordclass: DictsysWordclass,
    inflectionScheme: DictsysInflectionScheme
  ): void => {
    if (isSchemeOpen(id) && state.opened[id].inflectionScheme.id === inflectionScheme.id) {
      delete state.opened[id];
    } else {
      state.opened[id] = { headWord, wordclass, inflectionScheme };
      MatomoHelper.sendEvent(
        MatomoEventCategory.Results,
        MatomoEventAction.OpenInflectionScheme,
        "Clicked to open inflection scheme"
      );
    }
  };

  const getHeadWord = (id: string): string => state.opened[id].headWord;
  const getScheme = (id: string): DictsysInflectionScheme => state.opened[id].inflectionScheme;
  const getWordclass = (id: string): DictsysWordclass => state.opened[id].wordclass;

  const isAnySchemeOpen = (id: string[]): boolean => id.some(isSchemeOpen);
  const isSchemeOpen = (id: string): boolean => id in state.opened;

  const clearSchemeOpened = (): void => {
    state.opened = {};
  };

  const closeScheme = (id: string): void => {
    delete state.opened[id];
  };

  return {
    canShowInflectionScheme,
    clearSchemeOpened,
    closeScheme,
    getHeadWord,
    getScheme,
    getWordclass,
    isAnySchemeOpen,
    isSchemeOpen,
    toggleScheme
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useDictsysInflectionSchemeStore, import.meta.hot));
}
